/**
 * API client for HQ -> New Joiners (staff onboarding tracker).
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/new_joiners/. Every `apiEndpoints.X` reference lives
 * INSIDE a function body (TDZ-safety — mirrors parentOnboarding.js).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// {roles, steps, centres, joiners}
const fetchNewJoiners = async (role) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.newJoiners}/alfred`, role ? { role } : {})
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching new joiners')
    throw error
  }
}

// {name, role, centre, joining_date} -> the new joiner row
const createNewJoiner = async (body) => {
  try {
    const response = await post(`${apiEndpoints.newJoiners}/alfred`, body)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating new joiner')
    throw error
  }
}

// Edit details and/or set one step: {step, status}
const updateNewJoiner = async (uuid, body) => {
  try {
    const response = await patch(
      `${apiEndpoints.newJoiners}/${uuid}/alfred`,
      body
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating new joiner')
    throw error
  }
}

const deleteNewJoiner = async (uuid) => {
  try {
    await remove(`${apiEndpoints.newJoiners}/${uuid}/alfred`)
  } catch (error) {
    sentryLogger(error, 'error in deleting new joiner')
    throw error
  }
}

// Send one onboarding email now (kind: offer_letter | pre_joining | welcome).
// Resolves to the updated joiner row.
const sendNewJoinerEmail = async (uuid, kind) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/${uuid}/emails/${kind}/send/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending new joiner email')
    throw error
  }
}

// Stop (held=true) or let the scheduled job send this email.
const holdNewJoinerEmail = async (uuid, kind, held) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/${uuid}/emails/${kind}/hold/alfred`,
      { held }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in holding new joiner email')
    throw error
  }
}

// {subject, html, has_letter}
const previewNewJoinerEmail = async (uuid, kind) => {
  try {
    const response = await get(
      `${apiEndpoints.newJoiners}/${uuid}/emails/${kind}/preview/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in previewing new joiner email')
    throw error
  }
}

// The offer letter PDF as a Blob (it needs the auth header, so no plain link).
const fetchOfferLetterPdf = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.newJoiners}/${uuid}/offer_letter/alfred`,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching offer letter pdf')
    throw error
  }
}

/* THE JOINER's page. The token is the authorisation — the joiner has no
   Alfred login, and these two calls are the only ones the public route makes. */
const fetchJoinerUpload = async (token) => {
  try {
    const response = await get(`${apiEndpoints.newJoiners}/public/${token}`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching joiner upload page')
    throw error
  }
}

// The joiner signs online: {full_name, signature (PNG data URL), accept}.
const signOffer = async (token, body) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/public/${token}`,
      body
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in signing offer')
    throw error
  }
}

// The joiner asks HR something from the signing page:
// {action: 'date_change', new_date, reason} | {action: 'decline', reason}
// | {action: 'question', text}. Returns the page payload.
const sendJoinerRequest = async (token, body) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/public/${token}`,
      body
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending joiner request')
    throw error
  }
}

// HR closes one of the joiner's requests. Returns the joiner row.
const decideNewJoinerRequest = async (uuid, requestId, decision, note = '') => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/${uuid}/requests/${requestId}/decide/alfred`,
      { decision, note }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in deciding new joiner request')
    throw error
  }
}

/* THE JOINER's joining-details form (Keka + ID card). Token-authorised, no
   login, like the signing page. */
const fetchJoiningDetailsForm = async (token) => {
  try {
    const response = await get(`${apiEndpoints.newJoiners}/details/${token}`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching joining details form')
    throw error
  }
}

// formData: every answer plus the four uploads.
const submitJoiningDetails = async (token, formData) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/details/${token}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in submitting joining details')
    throw error
  }
}

// {submitted, at, rows: [{section, key, label, value, masked, document}]}
const fetchNewJoinerDetails = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.newJoiners}/${uuid}/details/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching new joiner details')
    throw error
  }
}

// One masked number in full (logged in the joiner's activity).
const revealNewJoinerDetail = async (uuid, key) => {
  try {
    const response = await post(
      `${apiEndpoints.newJoiners}/${uuid}/details/reveal/alfred`,
      { key }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in revealing new joiner detail')
    throw error
  }
}

// {url}: a 5-minute link to one uploaded document (logged).
const fetchNewJoinerDocument = async (uuid, key) => {
  try {
    const response = await get(
      `${apiEndpoints.newJoiners}/${uuid}/documents/${key}/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching new joiner document')
    throw error
  }
}

const fetchIdCardPdf = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.newJoiners}/${uuid}/id_card/alfred`,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching id card pdf')
    throw error
  }
}

export {
  fetchJoiningDetailsForm,
  submitJoiningDetails,
  fetchNewJoinerDetails,
  revealNewJoinerDetail,
  fetchNewJoinerDocument,
  fetchIdCardPdf,
  fetchNewJoiners,
  createNewJoiner,
  updateNewJoiner,
  deleteNewJoiner,
  sendNewJoinerEmail,
  holdNewJoinerEmail,
  previewNewJoinerEmail,
  fetchOfferLetterPdf,
  fetchJoinerUpload,
  signOffer,
  sendJoinerRequest,
  decideNewJoinerRequest
}
